import '../financials/financialsResponsive.css';
import ResponsiveTable, { ResponsiveCell } from '../shared/ResponsiveTable';
import ResponsiveRow from '../shared/ResponsiveRow';
import { useState, useEffect, useMemo, useCallback } from 'react';

function ChartTableView({ chartOfAccounts, onUpdate, onDelete, canEdit = true }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedAccounts, setSelectedAccounts] = useState(new Set());
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
     const toggleSelectAccount = (id) => {
        const newSelected = new Set(selectedAccounts);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedAccounts(newSelected);
    };
     const toggleSelectAll = () => {
        if (selectedAccounts.size === chartOfAccounts.length) {
            setSelectedAccounts(new Set());
        } else {
            setSelectedAccounts(new Set(chartOfAccounts.map(a => a.id)));
        }
    };
     const filteredAccounts = chartOfAccounts.filter(account =>
        account.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
     return (
        <div className="ia-financials ia-chart-table bg-white rounded-lg shadow">
            {/* Top Controls */}
            <ResponsiveRow className="p-4 border-b border-gray-200 flex items-center gap-4">
                {canEdit && <button className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-2 text-sm">
                    Batch actions ▼
                </button>}
                
                <div className="min-w-0 flex-1 flex items-center gap-2 border border-gray-300 rounded px-3 py-2">
                    <span>🔍</span>
                    <input 
                        type="text"
                        placeholder="Filter by name or number"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="min-w-0 flex-1 outline-none text-sm"
                    />
                </div>
                 <select className="px-4 py-2 border border-gray-300 rounded text-sm">
                    <option>All</option>
                    <option>Income</option>
                    <option>Expense</option>
                </select>
                 {canEdit && <button className="p-2 text-gray-600 hover:text-gray-800" title="Edit">✏️</button>}
                <button className="p-2 text-gray-600 hover:text-gray-800" title="Print">🖨️</button>
                <button className="p-2 text-gray-600 hover:text-gray-800" title="Settings">⚙️</button>
            </ResponsiveRow>
             {/* Table */}
            <div className="overflow-x-auto">
                <ResponsiveTable className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            {canEdit && <th className="px-4 py-3 text-left">
                                <label className="ia-table-select">
                                    <input type="checkbox" aria-label="Select all accounts"
                                        checked={selectedAccounts.size === filteredAccounts.length && filteredAccounts.length > 0}
                                        onChange={toggleSelectAll} />
                                </label>
                            </th>}
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                                NAME ↕
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                                ACCOUNT TYPE ↕
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                                DETAIL TYPE ↕
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">
                                BALANCE ↕
                            </th>
                            {canEdit && <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase">
                                ACTION
                            </th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {filteredAccounts.map((account, idx) => (
                            <tr key={account.id || idx} className="hover:bg-gray-50">
                                {canEdit && <ResponsiveCell label="Select" className="px-4 py-3">
                                    <label className="ia-table-select">
                                        <input type="checkbox" aria-label={`Select ${account.name}`}
                                            checked={selectedAccounts.has(account.id)}
                                            onChange={() => toggleSelectAccount(account.id)} />
                                    </label>
                                </ResponsiveCell>}
                                <ResponsiveCell label="Name" className="px-4 py-3">
                                    <div className="font-medium">{account.name}</div>
                                    {account.code && (
                                        <div className="text-sm text-gray-500">({account.code})</div>
                                    )}
                                </ResponsiveCell>
                                <ResponsiveCell label="Account type" className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        <span>🏦</span>
                                        <span className="capitalize">{account.type || 'N/A'}</span>
                                    </div>
                                </ResponsiveCell>
                                <ResponsiveCell label="Detail type" className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        <span>✓</span>
                                        <span>{account.category || account.description || 'General'}</span>
                                    </div>
                                </ResponsiveCell>
                                <ResponsiveCell label="Balance" className="px-4 py-3 text-right font-medium">
                                    ${account.balance?.toLocaleString() || '0.00'}
                                </ResponsiveCell>
                                {canEdit && <ResponsiveCell label="Action" className="px-4 py-3 text-center">
                                    <div className="flex items-center justify-center gap-2">
                                        <button 
                                            onClick={() => onUpdate(account)}
                                            className="px-3 py-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                                        >
                                            Edit
                                        </button>
                                        <button 
                                            onClick={() => onDelete(account.id)}
                                            className="px-3 py-1 text-red-600 hover:text-red-700 text-sm font-medium"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </ResponsiveCell>}
                            </tr>
                        ))}
                        {filteredAccounts.length === 0 && (
                            <tr>
                                <td colSpan={canEdit ? 6 : 4} className="px-4 py-8 text-center text-gray-500">
                                    No accounts found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </ResponsiveTable>
            </div>
             {/* Pagination */}
            <div className="p-4 border-t border-gray-200 flex items-center justify-center gap-4 text-sm">
                <button className="px-4 py-2 text-gray-600 hover:text-gray-800">← Previous</button>
                <span className="text-gray-600">1 - {filteredAccounts.length}</span>
                <button className="px-4 py-2 text-gray-600 hover:text-gray-800">Next →</button>
            </div>
        </div>
    );
}


export default ChartTableView;
